import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    EMPTY_SETTINGS, loadSettings, loadAnswers, loadMessages, loadConversationPage, loadVoices, saveSettings,
    addAnswer as apiAddAnswer, removeAnswer as apiRemoveAnswer,
    readDone, markDone, markNotDone, rememberSoulText, readParked, park, neverUsed, todayCounts
} from './api';
import { parseSoul } from './soul';

// Pages a look-back goes through before giving up, about 240 conversations.
const LOOK_BACK = 12;

/**
 * Everything the front desk shows, loaded once the Bailey tab knows this
 * person may see it, and shared by the home (the row, the messages), the
 * front desk page, the switch-on and the soul page.
 */
export default function useFrontDesk({ enabled }) {
    const [settings, setSettings] = useState(EMPTY_SETTINGS);
    const [answers, setAnswers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [conversations, setConversations] = useState([]);
    // Whether older conversations may exist, and whether a page is loading.
    const [convMore, setConvMore] = useState(true);
    const [convBusy, setConvBusy] = useState(false);
    // Looking back through the pages for a message's conversation.
    const [lookingBack, setLookingBack] = useState(false);
    // The voices callers can hear: null until asked, then { voices, defaultId } or { error }.
    const [voices, setVoices] = useState(null);
    const [done, setDone] = useState(readDone);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [tick, setTick] = useState(0);
    const [saving, setSaving] = useState(0);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    // What the server last agreed to, and whether it has been read at all.
    // Saves send the whole object, so one sent before the read (or after a
    // failed one) would wipe the salon's number, soul and switches.
    const latest = useRef(EMPTY_SETTINGS);
    const trusted = useRef(false);
    const queue = useRef(Promise.resolve());
    // How many conversations the server sends a page, learnt from the first.
    const pageSize = useRef(20);
    // The conversations so far, the last page loaded and whether there are
    // more, kept in refs too so a look-back can page on without waiting for
    // a render. `gen` moves on with each refresh, so a page that arrives
    // from before one is dropped.
    const convList = useRef([]);
    const lastPage = useRef(0);
    const more = useRef(true);
    const gen = useRef(0);
    const paging = useRef(null);
    // Conversations a message wants opened, and those a look-back gave up on.
    const wanted = useRef(new Set());
    const missed = useRef(new Set());
    const looking = useRef(null);

    const refresh = useCallback(async () => {
        const [s, a, m, c, v] = await Promise.all([
            loadSettings().then((x) => ({ ok: true, v: x })).catch((e) => ({ ok: false, e })),
            loadAnswers().catch(() => []),
            loadMessages().catch(() => []),
            loadConversationPage(0).catch(() => ({ list: [], size: 0 })),
            loadVoices().catch(() => ({ error: true, voices: [], defaultId: null }))
        ]);
        if (!alive.current) return;
        if (s.ok) { latest.current = s.v; trusted.current = true; setSettings(s.v); setError(''); rememberSoulText(s.v.greeting); }
        else { trusted.current = false; setError('I couldn’t load the front desk just now.'); }
        setAnswers(a);
        setMessages(m);
        gen.current += 1;
        paging.current = null;
        convList.current = c.list;
        lastPage.current = 0;
        missed.current = new Set();
        setConversations(c.list);
        // The first page says how big a page is; a shorter one later is the last.
        pageSize.current = c.size || pageSize.current;
        more.current = c.size > 0;
        setConvMore(more.current);
        setVoices(v);
        setLoaded(true);
    }, []);

    useEffect(() => { if (enabled) refresh(); }, [enabled, refresh]);

    // Saves the whole settings object with the patch applied; the name is
    // always Bailey (api.js). One at a time, each on top of the last, so two
    // quick switches can't each send the other's old value.
    const save = useCallback((patch) => {
        const run = queue.current.catch(() => {}).then(async () => {
            if (!trusted.current) throw new Error('I haven’t loaded your front desk yet, so nothing was changed. Try again in a moment.');
            const next = await saveSettings({ ...latest.current, ...patch });
            latest.current = next;
            if (alive.current) setSettings(next);
            rememberSoulText(next.greeting);
            return next;
        });
        queue.current = run;
        setSaving((n) => n + 1);
        const done = () => { if (alive.current) setSaving((n) => Math.max(0, n - 1)); };
        run.then(done, done);
        return run;
    }, []);

    /** After the server changed something itself (a number taken or given back). */
    const patchLocal = useCallback((patch) => {
        latest.current = { ...latest.current, ...patch };
        setSettings(latest.current);
    }, []);

    // One id or several: a person's messages are done together.
    const markMessageDone = useCallback((ids) => { setDone(markDone(ids)); }, []);
    const markMessageOpen = useCallback((ids) => { setDone(markNotDone(ids)); }, []);

    const reloadVoices = useCallback(async () => {
        setVoices(null);
        const v = await loadVoices().catch(() => ({ error: true, voices: [], defaultId: null }));
        if (alive.current) setVoices(v);
    }, []);

    // The next page of conversations, kept here so it survives leaving the
    // page. One at a time, shared by "Older conversations" and the look-back.
    // False when it couldn't be loaded, so the page can say so.
    const loadMoreConversations = useCallback(() => {
        if (paging.current) return paging.current;
        if (!more.current) return Promise.resolve(true);
        const g = gen.current;
        const page = lastPage.current + 1;
        setConvBusy(true);
        const run = loadConversationPage(page).then(({ list, size }) => {
            if (!alive.current || g !== gen.current) return true;
            const seen = new Set(convList.current.map((x) => x.id));
            const fresh = list.filter((x) => !seen.has(x.id));
            if (fresh.length) { convList.current = convList.current.concat(fresh); setConversations(convList.current); }
            lastPage.current = page;
            // A page the server left some out of still counts as full.
            if (size < pageSize.current) { more.current = false; setConvMore(false); }
            return true;
        }, () => false).then((ok) => {
            if (g === gen.current) paging.current = null;
            if (alive.current) setConvBusy(false);
            return ok;
        });
        paging.current = run;
        return run;
    }, []);

    // Messages only know their conversation's id, and conversations come 20
    // at a time, newest first, so a message's can be pages back. This pages
    // on while a wanted one is missing, at most LOOK_BACK pages a go.
    // (docs/FRONT_DESK_BACKEND.md asks for one conversation by its id.)
    const lookBack = useCallback(() => {
        if (looking.current) return looking.current;
        const has = (id) => convList.current.some((c) => c.id === id);
        const run = (async () => {
            setLookingBack(true);
            let pages = 0;
            while (alive.current && more.current && pages < LOOK_BACK && [...wanted.current].some((id) => !has(id))) {
                // eslint-disable-next-line no-await-in-loop
                if (!(await loadMoreConversations())) break;
                pages += 1;
            }
            wanted.current.forEach((id) => { if (!has(id)) missed.current.add(id); });
            wanted.current = new Set();
            looking.current = null;
            if (alive.current) setLookingBack(false);
        })();
        looking.current = run;
        return run;
    }, [loadMoreConversations]);

    /** A message's conversation, from what's loaded or by looking back. Null when it can't be found. */
    const findConversation = useCallback(async (id) => {
        const hit = () => (id && convList.current.find((c) => c.id === id)) || null;
        if (!id || hit()) return hit();
        missed.current.delete(id);
        wanted.current.add(id);
        await lookBack();
        // A look-back already under way may have stopped before this one.
        if (!hit() && more.current && alive.current) { missed.current.delete(id); wanted.current.add(id); await lookBack(); }
        return hit();
    }, [lookBack]);

    /** Looks back for these in the background, so their next steps are ready. */
    const wantConversations = useCallback((ids) => {
        let fresh = false;
        (ids || []).forEach((id) => {
            if (!id || missed.current.has(id) || wanted.current.has(id) || convList.current.some((c) => c.id === id)) return;
            wanted.current.add(id);
            fresh = true;
        });
        if (fresh) lookBack();
    }, [lookBack]);

    /** True once a conversation is in, or was looked for and not found, so nothing that depends on it will change. */
    const conversationSettled = useCallback((id) => !id || convList.current.some((c) => c.id === id) || missed.current.has(id), []);

    const addAnswer = useCallback(async (question, answer) => {
        const list = await apiAddAnswer(question, answer);
        if (alive.current) setAnswers(list);
        return list;
    }, []);

    const removeAnswer = useCallback(async (id) => {
        await apiRemoveAnswer(id);
        if (alive.current) setAnswers((rows) => rows.filter((r) => String(r._id) !== String(id)));
    }, []);

    // There's no way to edit an answer on the server yet
    // (docs/FRONT_DESK_BACKEND.md), so a change adds the new one first and
    // only then removes the old, so a failure never loses what was there.
    // If only the removing fails, the new one is kept and shown, and the
    // error says so: trying again would add it twice.
    const changeAnswer = useCallback(async (old, next) => {
        const list = await apiAddAnswer(next.question, next.answer);
        if (alive.current) setAnswers(list);
        try { await apiRemoveAnswer(old._id); }
        catch (e) {
            const err = new Error('I saved the new answer, but the old one is still there. Open it to remove it.');
            err.partial = true;
            throw err;
        }
        if (alive.current) setAnswers(list.filter((r) => String(r._id) !== String(old._id)));
    }, []);

    // The parked switch-on is read fresh whenever something changes, so a
    // "Not now" on the setup shows on the home straight away.
    const bump = useCallback(() => setTick((t) => t + 1), []);

    const open = useMemo(() => messages.filter((m) => !done[m.id]), [messages, done]);
    const conversationsById = useMemo(() => conversations.reduce((o, c) => { o[c.id] = c; return o; }, {}), [conversations]);
    const soul = useMemo(() => parseSoul(settings.greeting), [settings.greeting]);
    const counts = useMemo(() => todayCounts(conversations), [conversations]);
    const parked = useMemo(() => readParked(), [tick, loaded]); // eslint-disable-line react-hooks/exhaustive-deps
    const on = !!(settings.enabled || settings.voiceEnabled);
    const never = loaded && !error && neverUsed(settings, answers);
    // Answering already: a switch-on left part-way is finished with.
    useEffect(() => { if (loaded && !error && on && readParked()) { park(null); bump(); } }, [loaded, error, on, bump]);
    // Loaded and trusted: the only state in which anything may be saved.
    const ready = loaded && !error;

    return {
        loaded, ready, error, settings, answers, messages, open, done, conversations, conversationsById, counts, soul, on, never, parked, voices,
        conversationsMore: convMore, conversationsBusy: convBusy, loadMoreConversations, reloadVoices,
        findConversation, wantConversations, conversationSettled, lookingBack,
        saving: saving > 0, refresh, save, patchLocal, markMessageDone, markMessageOpen, addAnswer, removeAnswer, changeAnswer, bump
    };
}
